import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { isNumberFormat, toSafeInt } from "widgets/utils";

import { type ReduxAction, type ReduxDispatch } from "../../redux/redux-utils.js";

/**
 * Check if `id` can be parsed as a number and `dispatch` the `fn` argument.
 * If the returned value of the returned function call is `null`, it is because the id param is not a number.
 * Usually for Selector, the dispatch call returns a `Promise`, so we return `null` if `id` is not a number
 * so that we can use the optional chaining `()?.then()` if the loading succeed.
 */
const useLoadDispatchWithIdAsNumber = <T>(
  fn: (id: number) => ReduxAction<T>,
  id: string | number,
): (() => T | null) => {
  const dispatch = useDispatch<ReduxDispatch>();
  return useCallback((): T | null => {
    const _id = id;
    if (isNumberFormat(_id)) {
      return dispatch(fn(typeof _id === "string" ? toSafeInt(_id) : _id));
    }
    return null;
  }, [dispatch, fn, id]);
};

export { useLoadDispatchWithIdAsNumber };
